// The law's evaluation semantics: a small, total, deterministic rule language over claims.
// It knows only generic operators (filter, count, sum, exists, compare, arithmetic, hash, follow a reference,
// quantify over a list). Domain words never appear here; they live in predicates and policy data.

import { canonical } from './claim.ts';
import type { Claim, Json, UnsignedClaim } from './claim.ts';
import { sha256hex } from './sha256.ts';

// v2.1: `admittedAt` is the receipted admission time of a claim already in the log (the candidate's is $now). Policies that
// key a window on time must read it, never `issuedAt`, which the author writes.
export type Field = 'author' | 'subject' | 'predicate' | 'issuedAt' | 'admittedAt' | 'audience' | 'scope' | `ref${number}` | 'body' | `body.${string}`;
export type Op = 'eq' | 'gt' | 'gte' | 'lt' | 'lte';

export type V =
  | '$author'
  | '$subject'
  | '$ref'
  | '$self'
  | '$now'
  | '$item' // the element under quantification (each / some)
  | { lit: Json }
  | { param: string }
  | { get: Field; of: V }
  | { if: C; then: V; else: V }
  | { count: Filter; distinct?: 'author' }
  | { sum: Filter; field: Field }
  | { add: [V, V] }
  | { sub: [V, V] }
  | { mul: [V, V] }
  | { hash: V };

// A referencing claim that cancels a match: a predicate name, or a filter the referencing claim must satisfy. The
// filter may itself carry unrefBy ("a take that was not itself undone"); its `refs` key is redundant, since the
// referencing claim is already known to refer to the match.
export type UnrefSpec = string | Filter;

export interface Filter {
  predicate?: string | string[];
  author?: V;
  authorIn?: V;
  subject?: V;
  refs?: V; // v2.1: the claim's refs[0] (the one a rule binds as $ref). Positions 1.. are mentions.
  mentions?: V; // v2.1: the id appears at any position of refs
  admittedSince?: V; // at >= since
  admittedBefore?: V; // at < before
  where?: { field: Field; op: Op; value: V }[];
  unrefBy?: UnrefSpec[]; // v2.1: cancelled by a claim whose refs[0] is the match
  mentionedBy?: UnrefSpec[]; // v2.1: cancelled by a claim that lists the match at any position
}

// What a quantifier ranges over: the candidate's refs, an array in its body, or the claims a filter matches.
export type Over = 'refs' | Field | { match: Filter };

type Labeled<T> = T & { label?: string }; // a stable name carried into the trace (R1)

export type C =
  | true
  | Labeled<{ all: C[] }>
  | Labeled<{ any: C[] }>
  | Labeled<{ not: C }>
  | Labeled<{ eq: [V, V] }>
  | Labeled<{ in: [V, V] }>
  | Labeled<{ cmp: [V, Op, V] }>
  | Labeled<{ is: { claim: V; predicate: string } }>
  | Labeled<{ exists: Filter }>
  | Labeled<{ count: Filter; distinct?: 'author'; op: Op; n: V }>
  | Labeled<{ each: C; over: Over }> // every element passes; an empty list fails
  | Labeled<{ some: C; over: Over }>; // at least one element passes

export interface Policy {
  params: Record<string, Json>;
  rules: Record<string, C>;
}

export interface Entry {
  claim: Claim;
  seq: number;
  at: number;
  receipt?: Claim; // v2: the witness receipt, when the scope is witnessed
}

// In-memory indexes over the log. Optional in Ctx: without them match() scans the log; with them it starts
// from the narrowest list. Both give identical results (test/index.test.ts proves it by property).
export interface LogIndex {
  byPredicate: ReadonlyMap<string, readonly Entry[]>;
  byAuthor: ReadonlyMap<string, readonly Entry[]>;
  bySubject: ReadonlyMap<string, readonly Entry[]>;
  refsTo: ReadonlyMap<string, readonly Entry[]>; // entries whose refs include the key
}

export interface Ctx {
  cand: UnsignedClaim;
  log: readonly Entry[];
  byId: ReadonlyMap<string, Entry>;
  now: number;
  params: Record<string, Json>;
  index?: LogIndex;
  item?: Json; // bound by each / some
  meter?: Meter; // v2.2: set by run(); shared by every nested context
}

// v2.2 (bounded evaluation, REDTEAM-3 K4 / REDTEAM-2 row 15): one evaluation may inspect at most
// WORK_BASE + WORK_PER_ENTRY × |log| entries (filter candidates, reverse-index lists, lookups, quantifier elements).
// The bound is linear in the log, so a policy whose work grows faster than the log fails closed (rule-error) instead
// of making the single writer pay O(n^k). It is deterministic: replay meets the same budget at the same slot.
export const WORK_BASE = 1024;
export const WORK_PER_ENTRY = 64;
export interface Meter {
  left: number; // inspections still allowed
  nowReads: number; // how many times $now was read (marks time-dependent trace nodes)
}
export class WorkBudgetError extends Error {
  constructor() {
    super('evaluation work budget exhausted');
    this.name = 'WorkBudget';
  }
}
export function workBudget(logLength: number): number {
  return WORK_BASE + WORK_PER_ENTRY * logLength;
}
function spend(ctx: Ctx, k: number): void {
  const m = ctx.meter;
  if (!m) return;
  m.left -= k;
  if (m.left < 0) throw new WorkBudgetError();
}

// v2.2 (K1): trace nodes whose value depended on $now. Redaction for a third party drops `hidden` on them, so a chosen
// (or polled) evaluation time cannot turn a count of hidden claims into their admission times.
const TIMED = new WeakSet<TraceNode>();
export function isTimed(t: TraceNode): boolean {
  return TIMED.has(t);
}
function pushNode(trace: TraceNode[], node: TraceNode, ctx: Ctx, nowBefore: number): void {
  if (ctx.meter && ctx.meter.nowReads > nowBefore) TIMED.add(node);
  trace.push(node);
}
const nowReads = (ctx: Ctx): number => ctx.meter?.nowReads ?? 0;

export interface TraceNode {
  path: string;
  ok: boolean;
  label?: string; // the node's stable name, when the policy gave one (R1)
  matched?: string[]; // claim ids this node rested on
  hidden?: number; // ids removed by redaction (counts still reveal existence; see Scope)
  value?: Json; // the observed value: a count, a sum, the left side of a cmp (R2)
  n?: Json; // the resolved threshold or right side (R2)
  op?: Op;
}

const hasOwn = (o: object, k: string): boolean => Object.prototype.hasOwnProperty.call(o, k);

// Following a reference is provenance: the looked-up claim becomes evidence of the decision (L4).
function claimOf(v: V, ctx: Ctx, trace: TraceNode[], path: string): { claim: UnsignedClaim; at: number } | null {
  if (v === '$self') return { claim: ctx.cand, at: ctx.now };
  const id = val(v, ctx, trace, path);
  if (typeof id !== 'string') return null;
  spend(ctx, 1);
  const e = ctx.byId.get(id);
  trace.push({ path, ok: e !== undefined, matched: e ? [id] : [] });
  return e ? { claim: e.claim, at: e.at } : null;
}

const INDEX = /^(0|[1-9]\d*)$/;

export function read(c: UnsignedClaim, f: Field, at: number | null = null): Json {
  switch (f) {
    case 'author':
      return c.author;
    case 'subject':
      return c.subject;
    case 'predicate':
      return c.predicate;
    case 'issuedAt':
      return c.issuedAt;
    case 'admittedAt':
      return at;
    case 'audience':
      return c.audience;
    case 'scope':
      return c.scope ?? null;
    case 'body':
      return c.body;
  }
  if (f.startsWith('ref')) {
    const n = f.slice(3);
    return INDEX.test(n) ? (c.refs[Number(n)] ?? null) : null;
  }
  if (!f.startsWith('body.')) return null;
  let x: Json = c.body;
  for (const k of f.slice(5).split('.')) {
    if (x === null || typeof x !== 'object') return null;
    if (Array.isArray(x)) x = INDEX.test(k) ? (x[Number(k)] ?? null) : null;
    else x = hasOwn(x, k) ? (x[k] as Json) : null;
  }
  return x;
}

const num = (x: Json): number | null => (typeof x === 'number' && Number.isFinite(x) ? x : null);

// v2.2 (REDTEAM-3 L2): a non-number operand still gives null (total, documented), but finite operands whose result is
// not finite (1e308 × 2) are an evaluator error, so the verdict is rule-error (deny). Overflow never becomes a null
// that a negated comparison would read as "fine".
export class ArithmeticOverflow extends Error {
  constructor(where: string) {
    super(`arithmetic overflow in ${where}`);
    this.name = 'ArithmeticOverflow';
  }
}
function arith(pair: [V, V], f: (a: number, b: number) => number, ctx: Ctx, trace: TraceNode[], path: string): Json {
  const a = num(val(pair[0], ctx, trace, `${path}[0]`));
  const b = num(val(pair[1], ctx, trace, `${path}[1]`));
  if (a === null || b === null) return null;
  const r = f(a, b);
  if (!Number.isFinite(r)) throw new ArithmeticOverflow(path);
  return r;
}

export function val(v: V, ctx: Ctx, trace: TraceNode[], path: string): Json {
  if (v === '$author') return ctx.cand.author;
  if (v === '$subject') return ctx.cand.subject;
  if (v === '$ref') return ctx.cand.refs[0] ?? null;
  if (v === '$now') {
    if (ctx.meter) ctx.meter.nowReads++;
    return ctx.now;
  }
  if (v === '$item') return ctx.item ?? null;
  if (v === '$self') return null; // the candidate is reachable only through { get, of: '$self' }
  if (typeof v !== 'object' || v === null) return null;
  if ('lit' in v) return v.lit;
  if ('param' in v) return hasOwn(ctx.params, v.param) ? ctx.params[v.param]! : null;
  if ('get' in v) {
    const c = claimOf(v.of, ctx, trace, `${path}.of`);
    return c ? read(c.claim, v.get, c.at) : null;
  }
  if ('if' in v) {
    return test(v.if, ctx, trace, `${path}.if`) ? val(v.then, ctx, trace, `${path}.then`) : val(v.else, ctx, trace, `${path}.else`);
  }
  if ('count' in v) {
    const t0 = nowReads(ctx);
    const m = match(v.count, ctx, trace, path);
    const n = v.distinct === 'author' ? new Set(m.map((e) => e.claim.author)).size : m.length;
    pushNode(trace, { path, ok: true, matched: m.map((e) => e.claim.id), value: n }, ctx, t0);
    return n;
  }
  if ('sum' in v) {
    const t0 = nowReads(ctx);
    const m = match(v.sum, ctx, trace, path);
    let s = 0;
    for (const e of m) {
      const x = num(read(e.claim, v.field, e.at));
      if (x !== null) s += x;
    }
    if (!Number.isFinite(s)) throw new ArithmeticOverflow(path); // v2.2: a sum that overflows is an error, not null
    pushNode(trace, { path, ok: true, matched: m.map((e) => e.claim.id), value: s }, ctx, t0);
    return s;
  }
  if ('add' in v) return arith(v.add, (a, b) => a + b, ctx, trace, `${path}.add`);
  if ('sub' in v) return arith(v.sub, (a, b) => a - b, ctx, trace, `${path}.sub`);
  if ('mul' in v) return arith(v.mul, (a, b) => a * b, ctx, trace, `${path}.mul`);
  if ('hash' in v) return sha256hex(canonical(val(v.hash, ctx, trace, `${path}.hash`)));
  return null;
}

function same(a: Json, b: Json): boolean {
  return canonical(a) === canonical(b);
}

function compare(a: Json, op: Op, b: Json): boolean {
  if (op === 'eq') return same(a, b);
  if (typeof a !== 'number' || typeof b !== 'number') return false;
  return op === 'gt' ? a > b : op === 'gte' ? a >= b : op === 'lt' ? a < b : a <= b;
}

type Pred = (e: Entry) => boolean;

interface Compiled {
  test: Pred;
  author: Json | undefined;
  subject: Json | undefined;
  refs: Json | undefined;
  mentions: Json | undefined;
}

// Entries that list `id` anywhere in refs, from the reverse index when there is one.
function referencers(id: string, ctx: Ctx): readonly Entry[] {
  const l = ctx.index ? (ctx.index.refsTo.get(id) ?? []) : ctx.log.filter((e) => e.claim.refs.includes(id));
  spend(ctx, ctx.index ? l.length : ctx.log.length);
  return l;
}

const specs = (u: UnrefSpec[] | undefined, ctx: Ctx, trace: TraceNode[], path: string): Pred[] =>
  (u ?? []).map((s, i) => (typeof s === 'string' ? (e: Entry) => e.claim.predicate === s : compile(s, ctx, trace, `${path}[${i}]`).test));

// Evaluate the filter's values once (in the candidate's context) and return the per-entry predicate.
function compile(f: Filter, ctx: Ctx, trace: TraceNode[], path: string): Compiled {
  const preds = f.predicate === undefined ? null : Array.isArray(f.predicate) ? f.predicate : [f.predicate];
  const author = f.author === undefined ? undefined : val(f.author, ctx, trace, `${path}.author`);
  const authorIn = f.authorIn === undefined ? undefined : val(f.authorIn, ctx, trace, `${path}.authorIn`);
  const subject = f.subject === undefined ? undefined : val(f.subject, ctx, trace, `${path}.subject`);
  const refs = f.refs === undefined ? undefined : val(f.refs, ctx, trace, `${path}.refs`);
  const mentions = f.mentions === undefined ? undefined : val(f.mentions, ctx, trace, `${path}.mentions`);
  const since = f.admittedSince === undefined ? undefined : val(f.admittedSince, ctx, trace, `${path}.admittedSince`);
  const before = f.admittedBefore === undefined ? undefined : val(f.admittedBefore, ctx, trace, `${path}.admittedBefore`);
  const where = (f.where ?? []).map((w) => ({ ...w, v: val(w.value, ctx, trace, `${path}.where`) }));
  const unref = specs(f.unrefBy, ctx, trace, `${path}.unrefBy`);
  const mentioned = specs(f.mentionedBy, ctx, trace, `${path}.mentionedBy`);
  const test: Pred = ({ claim: c, at }) => {
    if (preds && !preds.includes(c.predicate)) return false;
    if (author !== undefined && c.author !== author) return false;
    if (authorIn !== undefined && !(Array.isArray(authorIn) && authorIn.includes(c.author))) return false;
    if (subject !== undefined && c.subject !== subject) return false;
    if (refs !== undefined && !(typeof refs === 'string' && c.refs[0] === refs)) return false;
    if (mentions !== undefined && !(typeof mentions === 'string' && c.refs.includes(mentions))) return false;
    if (since !== undefined && !(typeof since === 'number' && at >= since)) return false;
    if (before !== undefined && !(typeof before === 'number' && at < before)) return false;
    if (!where.every((w) => compare(read(c, w.field, at), w.op, w.v))) return false;
    if (unref.length > 0 && referencers(c.id, ctx).some((e) => e.claim.refs[0] === c.id && unref.some((p) => p(e)))) return false;
    if (mentioned.length > 0 && referencers(c.id, ctx).some((e) => mentioned.some((p) => p(e)))) return false;
    return true;
  };
  return { test, author, subject, refs, mentions };
}

// The narrowest indexed list that is a superset of the filter's matches, in log order. null = scan the log.
// Only string keys narrow; any other value (e.g. subject null) is left to the full predicate.
function candidates(f: Filter, ctx: Ctx, k: Compiled): readonly Entry[] | null {
  const ix = ctx.index;
  if (!ix) return null;
  const lists: (readonly Entry[])[] = [];
  if (f.predicate !== undefined) {
    const preds = [...new Set(Array.isArray(f.predicate) ? f.predicate : [f.predicate])];
    const parts = preds.map((p) => ix.byPredicate.get(p) ?? []);
    lists.push(parts.length === 1 ? parts[0]! : parts.flat().sort((a, b) => a.seq - b.seq));
  }
  if (typeof k.author === 'string') lists.push(ix.byAuthor.get(k.author) ?? []);
  if (typeof k.subject === 'string') lists.push(ix.bySubject.get(k.subject) ?? []);
  if (typeof k.refs === 'string') lists.push(ix.refsTo.get(k.refs) ?? []);
  if (typeof k.mentions === 'string') lists.push(ix.refsTo.get(k.mentions) ?? []);
  if (lists.length === 0) return null;
  return lists.reduce((best, l) => (l.length < best.length ? l : best));
}

export function match(f: Filter, ctx: Ctx, trace: TraceNode[], path: string): Entry[] {
  const k = compile(f, ctx, trace, path);
  const pool = candidates(f, ctx, k) ?? ctx.log;
  spend(ctx, pool.length);
  return pool.filter(k.test);
}

// The list a quantifier ranges over, and which of its elements are admitted claims (evidence).
function domain(over: Over, ctx: Ctx, trace: TraceNode[], path: string): Json[] {
  if (over === 'refs') return [...ctx.cand.refs];
  if (typeof over === 'string') {
    const x = read(ctx.cand, over);
    if (Array.isArray(x)) spend(ctx, x.length);
    return Array.isArray(x) ? [...x] : [];
  }
  return match(over.match, ctx, trace, path).map((e) => e.claim.id);
}

// v2.2 (REDTEAM-3 K3): all / any / each / some stop at the first deciding element. The verdict is the same; the trace
// holds only the nodes that were evaluated, so a failed guard is not followed by work it makes moot.
function every<T>(xs: readonly T[], f: (x: T, i: number) => boolean): boolean {
  for (let i = 0; i < xs.length; i++) if (!f(xs[i]!, i)) return false;
  return true;
}
function some<T>(xs: readonly T[], f: (x: T, i: number) => boolean): boolean {
  for (let i = 0; i < xs.length; i++) if (f(xs[i]!, i)) return true;
  return false;
}

export function test(c: C, ctx: Ctx, trace: TraceNode[], path = 'rule'): boolean {
  let ok: boolean;
  let node: Omit<TraceNode, 'path' | 'ok'> = {};
  const t0 = nowReads(ctx);
  if (c === true) ok = true;
  else if (typeof c !== 'object' || c === null) ok = false;
  else {
    if (typeof c.label === 'string') node.label = c.label;
    if ('all' in c) ok = every(c.all, (x, i) => test(x, ctx, trace, `${path}.all[${i}]`));
    else if ('any' in c) ok = some(c.any, (x, i) => test(x, ctx, trace, `${path}.any[${i}]`));
    else if ('not' in c) ok = !test(c.not, ctx, trace, `${path}.not`);
    else if ('eq' in c) ok = same(val(c.eq[0], ctx, trace, `${path}.eq[0]`), val(c.eq[1], ctx, trace, `${path}.eq[1]`));
    else if ('in' in c) {
      const x = val(c.in[0], ctx, trace, `${path}.in[0]`);
      const set = val(c.in[1], ctx, trace, `${path}.in[1]`);
      ok = Array.isArray(set) && set.some((y) => same(x, y));
    } else if ('cmp' in c) {
      const a = val(c.cmp[0], ctx, trace, `${path}.cmp[0]`);
      const b = val(c.cmp[2], ctx, trace, `${path}.cmp[2]`);
      ok = compare(a, c.cmp[1], b);
      node = { ...node, value: a, n: b, op: c.cmp[1] };
    } else if ('is' in c) ok = claimOf(c.is.claim, ctx, trace, `${path}.is`)?.claim.predicate === c.is.predicate;
    else if ('exists' in c) {
      const m = match(c.exists, ctx, trace, path);
      node.matched = m.map((e) => e.claim.id);
      ok = m.length > 0;
    } else if ('count' in c) {
      const m = match(c.count, ctx, trace, path);
      const n = c.distinct === 'author' ? new Set(m.map((e) => e.claim.author)).size : m.length;
      const threshold = val(c.n, ctx, trace, `${path}.n`);
      ok = compare(n, c.op, threshold);
      node = { ...node, matched: m.map((e) => e.claim.id), value: n, n: threshold, op: c.op };
    } else if ('each' in c || 'some' in c) {
      const isEach = 'each' in c;
      const items = domain(c.over, ctx, trace, `${path}.over`);
      const body = (item: Json, i: number): boolean => test(isEach ? c.each : c.some, { ...ctx, item }, trace, `${path}.${isEach ? 'each' : 'some'}[${i}]`);
      ok = isEach ? items.length > 0 && every(items, body) : some(items, body);
      node = { ...node, matched: items.filter((x): x is string => typeof x === 'string' && ctx.byId.has(x)), value: items.length };
    } else ok = false;
  }
  pushNode(trace, { path, ok, ...node }, ctx, t0);
  return ok;
}

// Total evaluation: an internal error (which validation should make impossible) is a verdict, never an exception.
export interface Verdict {
  ok: boolean;
  trace: TraceNode[];
  error?: string;
}

export function run(rule: C, ctx: Ctx): Verdict {
  const trace: TraceNode[] = [];
  const c: Ctx = ctx.meter ? ctx : { ...ctx, meter: { left: workBudget(ctx.log.length), nowReads: 0 } };
  try {
    return { ok: test(rule, c, trace), trace };
  } catch (e) {
    return { ok: false, trace, error: e instanceof Error ? `${e.name}: ${e.message}` : String(e) };
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Validation. A policy is admitted only if every node of every rule is well formed; the evaluator then has no
// undefined behaviour to meet. Depth is bounded so evaluation cannot overflow the stack.

export const MAX_DEPTH = 64;

export class PolicyError extends Error {
  readonly errors: string[];
  constructor(errors: string[]) {
    super(`invalid policy: ${errors[0]}${errors.length > 1 ? ` (+${errors.length - 1} more)` : ''}`);
    this.name = 'PolicyError';
    this.errors = errors;
  }
}

const OPS: readonly string[] = ['eq', 'gt', 'gte', 'lt', 'lte'];
const V_ATOMS: readonly string[] = ['$author', '$subject', '$ref', '$self', '$now', '$item'];
const V_KINDS: readonly string[] = ['lit', 'param', 'get', 'if', 'count', 'sum', 'add', 'sub', 'mul', 'hash'];
const C_KINDS: readonly string[] = ['all', 'any', 'not', 'eq', 'in', 'cmp', 'is', 'exists', 'count', 'each', 'some'];
const FILTER_KEYS: readonly string[] = ['predicate', 'author', 'authorIn', 'subject', 'refs', 'mentions', 'admittedSince', 'admittedBefore', 'where', 'unrefBy', 'mentionedBy'];
const PLAIN_FIELDS: readonly string[] = ['author', 'subject', 'predicate', 'issuedAt', 'admittedAt', 'audience', 'scope', 'body'];
// Params the kernel supplies at evaluation time, on top of the policy's own.
const BUILTIN_PARAMS: readonly string[] = ['scope'];
const LABEL: readonly string[] = ['label'];

const isObj = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x);

export function isField(f: unknown): f is Field {
  if (typeof f !== 'string') return false;
  if (PLAIN_FIELDS.includes(f)) return true;
  if (f.startsWith('ref')) return INDEX.test(f.slice(3));
  if (f.startsWith('body.')) return f.slice(5).split('.').every((seg) => seg.length > 0);
  return false;
}

export function isJson(x: unknown, depth = 0): x is Json {
  if (depth > MAX_DEPTH) return false;
  if (x === null || typeof x === 'boolean' || typeof x === 'string') return true;
  if (typeof x === 'number') return Number.isFinite(x);
  if (Array.isArray(x)) return x.every((y) => isJson(y, depth + 1));
  if (isObj(x)) return Object.getPrototypeOf(x) === Object.prototype || Object.getPrototypeOf(x) === null ? Object.values(x).every((y) => isJson(y, depth + 1)) : false;
  return false;
}

function onlyKeys(x: Record<string, unknown>, path: string, errs: string[], required: readonly string[], optional: readonly string[] = []): boolean {
  let ok = true;
  for (const k of required) {
    if (!hasOwn(x, k)) {
      errs.push(`${path}: missing "${k}"`);
      ok = false;
    }
  }
  for (const k of Object.keys(x)) {
    if (!required.includes(k) && !optional.includes(k)) {
      errs.push(`${path}: unknown key "${k}"`);
      ok = false;
    }
  }
  return ok;
}

// Validation context (v2.2). `names`: resolvable params. `params`: their values (numeric slots are type-checked).
// `quant`: inside an each/some body, where $item is bound. `labels`: labels already used on this evaluation path of
// the current rule (siblings of an `all`, ancestors); alternatives of an `any` may reuse a label, siblings may not.
interface Env {
  names: ReadonlySet<string>;
  params: Record<string, unknown>;
  quant: boolean;
  labels: Set<string>;
}

// v2.2 (REDTEAM-3 X5): only `$claim` envelopes are verified (L2). A rule that reads `author` or `sig` of a nested body
// object that is not a `$claim` envelope trusts bytes nobody signed; refuse it. (`body.author`, a top-level field of
// the candidate's own body, is plain data and stays allowed.)
function fieldLint(f: unknown, path: string, errs: string[]): void {
  if (typeof f !== 'string' || !f.startsWith('body.')) return;
  const segs = f.slice(5).split('.');
  if (segs.length < 2) return;
  const last = segs[segs.length - 1]!;
  if ((last === 'author' || last === 'sig') && segs[segs.length - 2] !== EMBED_KEY) {
    errs.push(`${path}: reads "${last}" of an unverified body object (${f}); embed the claim under "$claim" so the kernel verifies it`);
  }
}
const EMBED_KEY = '$claim';

// v2.2 (REDTEAM-3 G3): a value in a numeric position (a count threshold, an ordering comparison, arithmetic, an
// admission-time bound) that is a param must name a number. quorum: "2" used to validate and lock the council out.
function numericSlot(v: unknown, path: string, errs: string[], env: Env): void {
  if (!isObj(v)) return;
  if (typeof v.param === 'string' && hasOwn(env.params, v.param)) {
    const x = env.params[v.param];
    if (typeof x !== 'number' || !Number.isFinite(x)) errs.push(`${path}.param: "${v.param}" is used as a number but is ${JSON.stringify(x)}`);
  }
  if (isObj(v.if) || hasOwn(v, 'if')) {
    numericSlot(v.then, `${path}.then`, errs, env);
    numericSlot(v.else, `${path}.else`, errs, env);
  }
}

function checkV(v: unknown, path: string, errs: string[], depth: number, env: Env, asRef = false): void {
  if (depth > MAX_DEPTH) return void errs.push(`${path}: nesting deeper than ${MAX_DEPTH}`);
  if (typeof v === 'string') {
    if (!V_ATOMS.includes(v)) errs.push(`${path}: unknown value atom "${v}"`);
    // v2.2 (REDTEAM-3 L3): out of context these atoms are constant null and make clauses constant.
    else if (v === '$self' && !asRef) errs.push(`${path}: "$self" is only a claim to read from ({ get, of: "$self" } or { is: { claim: "$self" } })`);
    else if (v === '$item' && !env.quant) errs.push(`${path}: "$item" is only bound inside an each/some body`);
    return;
  }
  if (!isObj(v)) return void errs.push(`${path}: a value is an atom or an object`);
  const kinds = V_KINDS.filter((k) => hasOwn(v, k));
  if (kinds.length !== 1) return void errs.push(`${path}: a value has exactly one of ${V_KINDS.join('|')}`);
  const kind = kinds[0]!;
  switch (kind) {
    case 'lit':
      onlyKeys(v, path, errs, ['lit']);
      if (!isJson(v.lit)) errs.push(`${path}.lit: not JSON`);
      return;
    case 'param':
      onlyKeys(v, path, errs, ['param']);
      if (typeof v.param !== 'string' || v.param.length === 0) errs.push(`${path}.param: must be a name`);
      else if (!env.names.has(v.param)) errs.push(`${path}.param: unresolved param "${v.param}"`); // v2.1: no fail-open on a missing param
      return;
    case 'get':
      onlyKeys(v, path, errs, ['get', 'of']);
      if (!isField(v.get)) errs.push(`${path}.get: unknown field ${JSON.stringify(v.get)}`);
      else fieldLint(v.get, `${path}.get`, errs);
      checkV(v.of, `${path}.of`, errs, depth + 1, env, true);
      return;
    case 'if':
      onlyKeys(v, path, errs, ['if', 'then', 'else']);
      checkC(v.if, `${path}.if`, errs, depth + 1, env);
      checkV(v.then, `${path}.then`, errs, depth + 1, env);
      checkV(v.else, `${path}.else`, errs, depth + 1, env);
      return;
    case 'count':
      onlyKeys(v, path, errs, ['count'], ['distinct']);
      checkFilter(v.count, `${path}.count`, errs, depth + 1, env);
      if (hasOwn(v, 'distinct') && v.distinct !== 'author') errs.push(`${path}.distinct: must be "author"`);
      return;
    case 'sum':
      onlyKeys(v, path, errs, ['sum', 'field']);
      checkFilter(v.sum, `${path}.sum`, errs, depth + 1, env);
      if (!isField(v.field)) errs.push(`${path}.field: unknown field ${JSON.stringify(v.field)}`);
      else fieldLint(v.field, `${path}.field`, errs);
      return;
    case 'add':
    case 'sub':
    case 'mul': {
      onlyKeys(v, path, errs, [kind]);
      const pair = v[kind];
      if (!Array.isArray(pair) || pair.length !== 2) return void errs.push(`${path}.${kind}: needs [V, V]`);
      checkV(pair[0], `${path}.${kind}[0]`, errs, depth + 1, env);
      checkV(pair[1], `${path}.${kind}[1]`, errs, depth + 1, env);
      numericSlot(pair[0], `${path}.${kind}[0]`, errs, env);
      numericSlot(pair[1], `${path}.${kind}[1]`, errs, env);
      return;
    }
    case 'hash':
      onlyKeys(v, path, errs, ['hash']);
      checkV(v.hash, `${path}.hash`, errs, depth + 1, env);
      return;
  }
}

function checkFilter(f: unknown, path: string, errs: string[], depth: number, env: Env): void {
  if (depth > MAX_DEPTH) return void errs.push(`${path}: nesting deeper than ${MAX_DEPTH}`);
  if (!isObj(f)) return void errs.push(`${path}: a filter is an object`);
  for (const k of Object.keys(f)) if (!FILTER_KEYS.includes(k)) errs.push(`${path}: unknown filter key "${k}"`);
  if (hasOwn(f, 'predicate')) {
    const p = f.predicate;
    const okPred = typeof p === 'string' ? p.length > 0 : Array.isArray(p) && p.length > 0 && p.every((s) => typeof s === 'string' && s.length > 0);
    if (!okPred) errs.push(`${path}.predicate: a name or a list of names`);
  }
  for (const k of ['author', 'authorIn', 'subject', 'refs', 'mentions', 'admittedSince', 'admittedBefore'] as const) {
    if (hasOwn(f, k)) checkV(f[k], `${path}.${k}`, errs, depth + 1, env);
  }
  for (const k of ['admittedSince', 'admittedBefore'] as const) if (hasOwn(f, k)) numericSlot(f[k], `${path}.${k}`, errs, env);
  if (hasOwn(f, 'where')) {
    if (!Array.isArray(f.where)) errs.push(`${path}.where: a list`);
    else {
      f.where.forEach((w, i) => {
        const wp = `${path}.where[${i}]`;
        if (!isObj(w)) return void errs.push(`${wp}: an object { field, op, value }`);
        onlyKeys(w, wp, errs, ['field', 'op', 'value']);
        if (!isField(w.field)) errs.push(`${wp}.field: unknown field ${JSON.stringify(w.field)}`);
        else fieldLint(w.field, `${wp}.field`, errs);
        if (typeof w.op !== 'string' || !OPS.includes(w.op)) errs.push(`${wp}.op: unknown op ${JSON.stringify(w.op)}`);
        checkV(w.value, `${wp}.value`, errs, depth + 1, env);
        if (typeof w.op === 'string' && w.op !== 'eq') numericSlot(w.value, `${wp}.value`, errs, env);
      });
    }
  }
  for (const key of ['unrefBy', 'mentionedBy'] as const) {
    if (!hasOwn(f, key)) continue;
    const u = f[key];
    if (!Array.isArray(u) || u.length === 0) errs.push(`${path}.${key}: a list of names or filters`);
    else {
      u.forEach((s, i) => {
        if (typeof s === 'string') {
          if (s.length === 0) errs.push(`${path}.${key}[${i}]: empty name`);
        } else checkFilter(s, `${path}.${key}[${i}]`, errs, depth + 1, env);
      });
    }
  }
}

function checkOver(o: unknown, path: string, errs: string[], depth: number, env: Env): void {
  if (o === 'refs') return;
  if (typeof o === 'string') {
    if (!isField(o)) errs.push(`${path}: unknown field ${JSON.stringify(o)}`);
    return;
  }
  if (!isObj(o)) return void errs.push(`${path}: "refs", a field, or { match: Filter }`);
  onlyKeys(o, path, errs, ['match']);
  checkFilter(o.match, `${path}.match`, errs, depth + 1, env);
}

function checkC(c: unknown, path: string, errs: string[], depth: number, env: Env): void {
  if (depth > MAX_DEPTH) return void errs.push(`${path}: nesting deeper than ${MAX_DEPTH}`);
  if (c === true) return;
  if (!isObj(c)) return void errs.push(`${path}: a condition is true or an object`);
  if (hasOwn(c, 'label')) {
    if (typeof c.label !== 'string' || c.label.length === 0) errs.push(`${path}.label: a non-empty string`);
    // v2.2 (REDTEAM-3 L4): a label names one clause. Two clauses on one evaluation path with the same label make the
    // explanation ambiguous (and let an amendment pass one clause off as another). Alternatives of an `any` may share one.
    else if (env.labels.has(c.label)) errs.push(`${path}.label: "${c.label}" is already used on this path of the rule`);
    else env.labels.add(c.label);
  }
  const kinds = C_KINDS.filter((k) => hasOwn(c, k));
  if (kinds.length !== 1) return void errs.push(`${path}: a condition has exactly one of ${C_KINDS.join('|')}`);
  const kind = kinds[0]!;
  switch (kind) {
    case 'all':
    case 'any': {
      onlyKeys(c, path, errs, [kind], LABEL);
      const xs = c[kind];
      if (!Array.isArray(xs)) return void errs.push(`${path}.${kind}: a list of conditions`);
      if (kind === 'all') xs.forEach((x, i) => checkC(x, `${path}.${kind}[${i}]`, errs, depth + 1, env));
      else {
        const seen = new Set(env.labels);
        xs.forEach((x, i) => {
          const branch = new Set(seen);
          checkC(x, `${path}.${kind}[${i}]`, errs, depth + 1, { ...env, labels: branch });
          for (const l of branch) env.labels.add(l);
        });
      }
      return;
    }
    case 'not':
      onlyKeys(c, path, errs, ['not'], LABEL);
      checkC(c.not, `${path}.not`, errs, depth + 1, env);
      return;
    case 'eq':
    case 'in': {
      onlyKeys(c, path, errs, [kind], LABEL);
      const pair = c[kind];
      if (!Array.isArray(pair) || pair.length !== 2) return void errs.push(`${path}.${kind}: needs [V, V]`);
      checkV(pair[0], `${path}.${kind}[0]`, errs, depth + 1, env);
      checkV(pair[1], `${path}.${kind}[1]`, errs, depth + 1, env);
      return;
    }
    case 'cmp': {
      onlyKeys(c, path, errs, ['cmp'], LABEL);
      const t = c.cmp;
      if (!Array.isArray(t) || t.length !== 3) return void errs.push(`${path}.cmp: needs [V, op, V]`);
      checkV(t[0], `${path}.cmp[0]`, errs, depth + 1, env);
      if (typeof t[1] !== 'string' || !OPS.includes(t[1])) errs.push(`${path}.cmp[1]: unknown op ${JSON.stringify(t[1])}`);
      checkV(t[2], `${path}.cmp[2]`, errs, depth + 1, env);
      if (t[1] !== 'eq') {
        numericSlot(t[0], `${path}.cmp[0]`, errs, env);
        numericSlot(t[2], `${path}.cmp[2]`, errs, env);
      }
      return;
    }
    case 'is': {
      onlyKeys(c, path, errs, ['is'], LABEL);
      if (!isObj(c.is)) return void errs.push(`${path}.is: an object { claim, predicate }`);
      onlyKeys(c.is, `${path}.is`, errs, ['claim', 'predicate']);
      checkV(c.is.claim, `${path}.is.claim`, errs, depth + 1, env, true);
      if (typeof c.is.predicate !== 'string' || c.is.predicate.length === 0) errs.push(`${path}.is.predicate: a name`);
      return;
    }
    case 'exists':
      onlyKeys(c, path, errs, ['exists'], LABEL);
      checkFilter(c.exists, `${path}.exists`, errs, depth + 1, env);
      return;
    case 'count':
      onlyKeys(c, path, errs, ['count', 'op', 'n'], ['distinct', ...LABEL]);
      checkFilter(c.count, `${path}.count`, errs, depth + 1, env);
      if (typeof c.op !== 'string' || !OPS.includes(c.op)) errs.push(`${path}.op: unknown op ${JSON.stringify(c.op)}`);
      checkV(c.n, `${path}.n`, errs, depth + 1, env);
      numericSlot(c.n, `${path}.n`, errs, env);
      if (hasOwn(c, 'distinct') && c.distinct !== 'author') errs.push(`${path}.distinct: must be "author"`);
      return;
    case 'each':
    case 'some':
      onlyKeys(c, path, errs, [kind, 'over'], LABEL);
      checkC(c[kind], `${path}.${kind}`, errs, depth + 1, { ...env, quant: true });
      checkOver(c.over, `${path}.over`, errs, depth + 1, env);
      return;
  }
}

// Every problem in the body, with its path. Empty means the body is a well-formed policy.
export function validatePolicy(body: unknown): string[] {
  const errs: string[] = [];
  if (!isObj(body)) return ['policy: the body is an object { params, rules }'];
  onlyKeys(body, 'policy', errs, ['params', 'rules']);
  const names = new Set<string>(BUILTIN_PARAMS);
  const params: Record<string, unknown> = isObj(body.params) ? body.params : {};
  if (!isObj(body.params)) errs.push('policy.params: an object');
  else {
    for (const [k, v] of Object.entries(body.params)) {
      if (!isJson(v)) errs.push(`policy.params.${k}: not JSON`);
      names.add(k);
    }
    // v2.1 (g): the predicates a claim must be scope-bound to carry; the kernel refuses them with wrong-scope otherwise.
    if (hasOwn(body.params, 'boundPredicates')) {
      const b = body.params.boundPredicates;
      if (!Array.isArray(b) || !b.every((x) => typeof x === 'string' && x.length > 0)) errs.push('policy.params.boundPredicates: a list of predicate names');
    }
    // v2.2 (L3′ time bound): how far a receipt's time may differ from the claim's issuedAt, in ms.
    if (hasOwn(body.params, 'receiptSkew')) {
      const k = body.params.receiptSkew;
      if (typeof k !== 'number' || !Number.isFinite(k) || k <= 0) errs.push('policy.params.receiptSkew: a positive number of milliseconds');
    }
    if (hasOwn(body.params, 'witness') && !(typeof body.params.witness === 'string' && body.params.witness.length > 0)) errs.push('policy.params.witness: a key id');
  }
  if (!isObj(body.rules)) errs.push('policy.rules: an object of predicate -> condition');
  else {
    for (const [name, rule] of Object.entries(body.rules)) {
      if (name.length === 0) errs.push('policy.rules: empty predicate name');
      checkC(rule, `rules.${name}`, errs, 0, { names, params, quant: false, labels: new Set() });
    }
  }
  return errs;
}

export function assertPolicy(body: unknown): Policy {
  const errs = validatePolicy(body);
  if (errs.length > 0) throw new PolicyError(errs);
  return body as Policy;
}
